export class InfiniteScroll {
  private observer: IntersectionObserver;
  private isLoading = false;

  constructor(private element: HTMLElement) {
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        rootMargin: "100px 0px", // 在元素距离视口还有 100px 时就触发
      },
    );
    this.observer.observe(element);
  }

  /**
   * 处理元素进入视口的回调
   */
  private async handleIntersection(
    entries: IntersectionObserverEntry[],
  ): Promise<void> {
    const entry = entries[0];
    if (!entry?.isIntersecting) return; // 兼容 noUncheckedIndexedAccess
    if (this.isLoading) return; // 防止并发触发

    const nextUrl = this.element.dataset.nextUrl;
    if (!nextUrl) {
      // 没有下一页，清理自身
      this.observer.disconnect();
      this.element.remove();
      return;
    }

    // 支持通过 data-target 自定义追加目标，默认为 #articles:after
    const appendTarget = this.element.dataset.target || "#articles:after";

    this.isLoading = true;
    // 立即停止观察，防止重复触发（观察的元素即将被删除）
    this.observer.disconnect();

    // 关键：保留 sentinel 元素直到新内容到位再移除。
    // 若提前 remove()，slow 4G 下文档会先收缩、等待响应、再扩展，
    // 期间 flex 容器高度小于 sticky 右侧栏 h-[calc(100vh-6rem)]，
    // 触发 sticky 容纳块边界变化导致右侧栏上下抖动。
    try {
      await up.render({
        // 使用 :after 伪选择器，将响应中目标容器的子内容追加到现有列表末尾
        target: appendTarget,
        url: nextUrl,
        scroll: false,
        reveal: false,
        hungry: false,
      });
    } finally {
      // 渲染完成后再移除旧 sentinel，避免与新页面带来的 sentinel 重复累积
      this.element.remove();
      this.isLoading = false;
    }
    this.isLoading = false;
  }

  /**
   * 销毁组件，清理资源
   */
  destroy(): void {
    this.observer.disconnect();
  }

  /**
   * 注册 Unpoly 编译器
   * @param selector CSS 选择器，如 '.infinite-sentinel'
   */
  static compiler(selector: string): void {
    up.compiler(selector, (element: Element) => {
      const scroll = new InfiniteScroll(element as HTMLElement);
      return () => scroll.destroy();
    });
  }
}
